import { VALUE_CHAIN_MAP_CUSTOM_NODES, VALUE_CHAIN_UNIT_TYPES } from 'constants/interfaces';

import { getUnitIdFromNodeId } from '../../../../../../utils/AICreatorHelper';

import type { Node } from '@xyflow/react';
import type { TMonitoringSelectionMode } from '../../../../../../constants/interfaces/IMonitoringAgentDefinition';
import type { ICustomUnitNodeData } from '../interfaces/ICustomUnitNode';

const SUPPLIER_BADGE_UNIT_TYPES: ReadonlySet<string> = new Set([
  VALUE_CHAIN_UNIT_TYPES.SUPPLIER,
]);

const PRODUCT_BADGE_UNIT_TYPES: ReadonlySet<string> = new Set([
  VALUE_CHAIN_UNIT_TYPES.PRODUCT,
  VALUE_CHAIN_UNIT_TYPES.OUTPUT_PRODUCT,
]);

const CLIMATE_BADGE_UNIT_TYPES: ReadonlySet<string> = new Set([
  VALUE_CHAIN_UNIT_TYPES.SUPPLIER,
  VALUE_CHAIN_UNIT_TYPES.OWN_OPERATION,
  VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET,
]);

export const getMonitoringRiskBadgeUnitTypes = (
  selectionMode: TMonitoringSelectionMode = 'upstream-suppliers',
): ReadonlySet<string> => {
  if (selectionMode === 'products-mixed') {
    return PRODUCT_BADGE_UNIT_TYPES;
  }

  if (selectionMode === 'climate-mixed') {
    return CLIMATE_BADGE_UNIT_TYPES;
  }

  return SUPPLIER_BADGE_UNIT_TYPES;
};

const applyMonitoringRiskStateToNodes = (
  nodesToDecorate: Node[],
  atRiskUnitIds: Set<number>,
  isDraftReviewMode: boolean,
  selectionMode: TMonitoringSelectionMode = 'upstream-suppliers',
): Node[] => {
  if (isDraftReviewMode) {
    let hasClearedBadges = false;

    const clearedNodes = nodesToDecorate.map((nodeToDecorate) => {
      if (nodeToDecorate.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
        return nodeToDecorate;
      }

      const nodeData = nodeToDecorate.data as unknown as ICustomUnitNodeData;

      if (!nodeData.showMonitoringRiskBadge) {
        return nodeToDecorate;
      }

      hasClearedBadges = true;

      return {
        ...nodeToDecorate,
        data: {
          ...nodeData,
          showMonitoringRiskBadge: false,
        },
      };
    });

    return hasClearedBadges ? clearedNodes : nodesToDecorate;
  }

  const eligibleUnitTypes = getMonitoringRiskBadgeUnitTypes(selectionMode);
  let hasChanges = false;

  const nextNodes = nodesToDecorate.map((nodeToDecorate) => {
    if (nodeToDecorate.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      return nodeToDecorate;
    }

    const nodeData = nodeToDecorate.data as unknown as ICustomUnitNodeData;
    const unitId = getUnitIdFromNodeId(nodeToDecorate.id);
    const showMonitoringRiskBadge = eligibleUnitTypes.has(String(nodeData.type || ''))
      && unitId !== null
      && atRiskUnitIds.has(unitId);

    if (nodeData.showMonitoringRiskBadge === showMonitoringRiskBadge) {
      return nodeToDecorate;
    }

    hasChanges = true;

    return {
      ...nodeToDecorate,
      data: {
        ...nodeData,
        showMonitoringRiskBadge,
      },
    };
  });

  return hasChanges ? nextNodes : nodesToDecorate;
};

export default applyMonitoringRiskStateToNodes;
